import { Check, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import ExecutiveProfileCard from "@/components/team/ExecutiveProfileCard";
import StructuredData from "@/components/StructuredData";
import {
  COMPANY_LINKEDIN_URL,
  ORGANIZATION_NAME,
  ORGANIZATION_URL,
  TEAM_BOOKING_URL,
  TEAM_CALL_DISPLAY,
  TEAM_CALL_URL,
  administrationTeam,
  executiveLeadership,
  salesLeadership,
  technologyTeam,
  teamMembers,
} from "@/lib/teamData";

const trustBarItems = [
  "North Carolina’s First Black-Owned AI & Robotics Company",
  "Authorized PUDU Robotics Dealer",
  "Certified Robotics Specialists",
  "AI Automation Experts",
  "Local Deployment & Support Team",
  "Business Transformation Advisors",
];

const TeamSection = () => {
  const teamSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${ORGANIZATION_URL}/#team-organization`,
        name: ORGANIZATION_NAME,
        url: ORGANIZATION_URL,
        sameAs: [COMPANY_LINKEDIN_URL],
        department: [
          "Executive Leadership",
          "Sales & Business Transformation Leadership",
          "Technology & Robotics Team",
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${ORGANIZATION_URL}/#leadership`,
        name: "Neil AI Global Solutions Leadership Team",
        itemListElement: teamMembers.map((member, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${ORGANIZATION_URL}/team/${member.slug}`,
          item: {
            "@type": "Person",
            name: member.name,
            jobTitle: member.title,
            image: `${ORGANIZATION_URL}${member.image}`,
            url: `${ORGANIZATION_URL}/team/${member.slug}`,
            sameAs: [member.linkedin],
            worksFor: {
              "@type": "Organization",
              name: ORGANIZATION_NAME,
              url: ORGANIZATION_URL,
            },
            knowsAbout: member.expertise,
            homeLocation: {
              "@type": "Place",
              name: member.areasServed.join(", "),
            },
          },
        })),
      },
    ],
  };

  return (
    <section id="team" className="scroll-mt-32 py-20 md:py-28" aria-labelledby="team-heading">
      <StructuredData id="team-section" data={teamSchema} />

      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-4 text-xs font-bold tracking-[0.32em] text-primary">LEADERSHIP & EXPERTISE</p>
          <h2 id="team-heading" className="font-orbitron text-3xl font-bold leading-tight text-foreground md:text-5xl">
            Meet The Team Behind The Future
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
            The experts helping businesses leverage Artificial Intelligence, Automation, and Robotics to work smarter,
            serve customers better, and scale with confidence.
          </p>
        </div>

        <div className="mt-8 grid gap-3 rounded-[24px] border border-[hsl(var(--line))] bg-card/50 p-4 sm:grid-cols-2 lg:grid-cols-3 lg:p-5">
          {trustBarItems.map((item) => (
            <div
              key={item}
              className="flex min-h-[56px] items-center gap-3 rounded-[18px] border border-[hsl(var(--line))] bg-background/60 px-4 py-3 backdrop-blur-sm"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
                <Check className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium leading-snug text-foreground">{item}</span>
            </div>
          ))}
        </div>

        <div className="mt-12 space-y-10 md:mt-16">
          <div>
            <div className="mb-6">
              <h3 className="font-orbitron text-2xl font-bold text-foreground md:text-3xl">Executive Leadership</h3>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
                Senior leadership shaping Neil AI’s strategy, execution, growth, and long-term client success.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {executiveLeadership.map((member, index) => (
                <ExecutiveProfileCard key={member.slug} member={member} size="large" priority={index < 2} />
              ))}
            </div>
          </div>

          {administrationTeam.length > 0 && (
            <div>
              <div className="mb-6">
                <h3 className="font-orbitron text-2xl font-bold text-foreground md:text-3xl">Administration</h3>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
                  The administrative structure keeping leadership, clients, and deployments coordinated as Neil AI scales.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {administrationTeam.map((member) => (
                  <ExecutiveProfileCard key={member.slug} member={member} size="medium" />
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="mb-6">
              <h3 className="font-orbitron text-2xl font-bold text-foreground md:text-3xl">
                Sales & Business Transformation Leadership
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
                Advisors helping organizations identify where AI, automation, and robotics can create measurable impact.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {salesLeadership.map((member) => (
                <ExecutiveProfileCard key={member.slug} member={member} size="medium" />
              ))}
            </div>
          </div>

          <div>
            <div className="mb-6">
              <h3 className="font-orbitron text-2xl font-bold text-foreground md:text-3xl">Technology & Robotics Team</h3>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
                Certified technical support and deployment expertise for live robotics implementation.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {technologyTeam.map((member) => (
                <ExecutiveProfileCard key={member.slug} member={member} size="medium" />
              ))}
            </div>
          </div>
        </div>

        <div
          className="mt-14 overflow-hidden rounded-[28px] border border-[hsl(var(--line))] px-6 py-8 text-center md:mt-16 md:px-10 md:py-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 0%, hsl(var(--primary) / 0.18), transparent 34%), linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--background)) 100%)",
            boxShadow: "0 0 36px hsl(var(--primary) / 0.16), 0 18px 60px rgba(0,0,0,0.42)",
          }}
        >
          <h3 className="font-orbitron text-2xl font-bold text-foreground md:text-4xl">
            Ready To See What AI & Robotics Can Do For Your Business?
          </h3>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Schedule a free discovery call with a Neil AI specialist.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="min-h-12 rounded-full bg-primary px-8 text-primary-foreground shadow-[0_0_30px_hsl(var(--primary)/0.4)] hover:bg-primary/90"
            >
              <a href={TEAM_BOOKING_URL} target="_blank" rel="noopener noreferrer">
                BOOK A DEMO
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="min-h-12 rounded-full border-accent/50 bg-background/50 px-8 text-accent hover:bg-accent/10 hover:text-accent"
            >
              <a href={TEAM_CALL_URL}>
                <PhoneCall className="mr-2 h-4 w-4" />
                CALL {TEAM_CALL_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
